import { renewInvitation } from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import { useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { useTranslationAuth } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { authStartUrl } from '@dealstack/frontend/shared/pages/AuthStart/startHostedAuth';
import {
	INVITATION_EXPIRED_VARIANT,
	INVITATION_EXPIRED_VARIANT_PARAM,
	INVITATION_TOKEN_PARAM,
	isInvitationToken,
} from '@dealstack/shared/lib/common/invitationLink';
import { ApplicationType, InvitationRenewalOutcome } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Button, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type {
	PageCopyKeys,
	PageState,
} from '@dealstack/frontend/shared/pages/InvitationExpired/InvitationExpired.types';

const RENEW_COPY: PageCopyKeys = {
	titleKey: 'invitation_expired.renew.title',
	descriptionKey: 'invitation_expired.renew.description',
};

const SIGN_IN_COPY: PageCopyKeys = {
	titleKey: 'invitation_expired.sign_in.title',
	descriptionKey: 'invitation_expired.sign_in.description',
};

const COPY: Record<PageState, PageCopyKeys> = {
	renew: RENEW_COPY,
	pending: RENEW_COPY,
	sent: {
		titleKey: 'invitation_expired.sent.title',
		descriptionKey: 'invitation_expired.sent.description',
	},
	sign_in: SIGN_IN_COPY,
	invitation_pending: SIGN_IN_COPY,
	contact: {
		titleKey: 'invitation_expired.contact.title',
		descriptionKey: 'invitation_expired.contact.description',
	},
	failed: {
		titleKey: 'invitation_expired.failed.title',
		descriptionKey: 'invitation_expired.failed.description',
	},
};

const STATE_FOR_OUTCOME: Readonly<Record<InvitationRenewalOutcome, PageState>> = {
	[InvitationRenewalOutcome.SENT]: 'sent',
	[InvitationRenewalOutcome.SIGN_IN]: 'sign_in',
	[InvitationRenewalOutcome.INVITATION_PENDING]: 'invitation_pending',
	[InvitationRenewalOutcome.CONTACT_INVITER]: 'contact',
};

const stateForOutcome = (outcome: InvitationRenewalOutcome | undefined): PageState =>
	(outcome && STATE_FOR_OUTCOME[outcome]) ?? 'failed';

const RENEWABLE_STATES: ReadonlySet<PageState> = new Set(['renew', 'pending', 'failed']);
const SIGN_IN_STATES: ReadonlySet<PageState> = new Set([
	'sign_in',
	'invitation_pending',
	'contact',
]);

const tokenFrom = (hash: string, searchParams: URLSearchParams): string | null =>
	new URLSearchParams(hash.replace(/^#/, '')).get(INVITATION_TOKEN_PARAM) ??
	searchParams.get(INVITATION_TOKEN_PARAM);

const withoutToken = (pathname: string, searchParams: URLSearchParams): string => {
	const kept = new URLSearchParams(searchParams);
	kept.delete(INVITATION_TOKEN_PARAM);
	const query = kept.toString();

	return query ? `${pathname}?${query}` : pathname;
};

const initialStateOf = (variant: string | null, invitationToken: string | null): PageState => {
	if (variant === INVITATION_EXPIRED_VARIANT.RENEW && invitationToken) {
		return 'renew';
	}

	return 'contact';
};

const signInHrefFor = (
	state: PageState,
	apiUrl: string,
	applicationType: ApplicationType,
	invitationToken: string | null
): string => {
	if (state === 'invitation_pending' && invitationToken) {
		return authStartUrl(apiUrl, applicationType, invitationToken);
	}

	return authStartUrl(apiUrl, applicationType);
};

interface Props {
	applicationType: ApplicationType;
}

export const InvitationExpired = ({ applicationType }: Props) => {
	const tAuth = useTranslationAuth();
	const globalConfig = useGlobalConfig();
	const [searchParams] = useSearchParams();
	const { hash, pathname } = useLocation();
	const navigate = useNavigate();
	const [invitationToken] = useState<string | null>(() => {
		const tokenParam = tokenFrom(hash, searchParams);

		return isInvitationToken(tokenParam) ? tokenParam : null;
	});
	const [state, setState] = useState<PageState>(() =>
		initialStateOf(searchParams.get(INVITATION_EXPIRED_VARIANT_PARAM), invitationToken)
	);

	useEffect(() => {
		if (tokenFrom(hash, searchParams) !== null) {
			navigate(withoutToken(pathname, searchParams), { replace: true });
		}
	}, [hash, pathname, searchParams, navigate]);

	const requestRenewal = () => {
		if (!invitationToken) {
			return;
		}

		setState('pending');
		renewInvitation({ invitationToken, applicationType })
			.then((renewal) => setState(stateForOutcome(renewal?.outcome)))
			.catch(() => setState('failed'));
	};

	const { titleKey, descriptionKey } = COPY[state];

	return (
		<div
			className="flex flex-col p-8 gap-8 items-center w-[424px] bg-[#F4F5F6] rounded-2xl border border-[#E9EBED]"
			data-testid={TEST_ELEMENT_IDS.INVITATION_EXPIRED_PAGE}
		>
			<div className="flex flex-col items-center">
				<Text size="B3Bold" c="dark.8" ta="center" className="mb-[12px] text-balance">
					{tAuth(titleKey)}
				</Text>
				<Text size="B5Medium" c="dark.0" ta="center">
					{tAuth(descriptionKey)}
				</Text>
			</div>
			{RENEWABLE_STATES.has(state) && (
				<Button
					type="button"
					variant="primary"
					loading={state === 'pending'}
					onClick={requestRenewal}
					data-testid={TEST_ELEMENT_IDS.INVITATION_EXPIRED_RENEW_BUTTON}
				>
					{tAuth('invitation_expired.renew.action')}
				</Button>
			)}
			{SIGN_IN_STATES.has(state) && (
				<Button
					component="a"
					variant="primary"
					href={signInHrefFor(
						state,
						globalConfig.apiUrl,
						applicationType,
						invitationToken
					)}
					data-testid={TEST_ELEMENT_IDS.INVITATION_EXPIRED_SIGN_IN_BUTTON}
				>
					{tAuth('invitation_expired.sign_in.action')}
				</Button>
			)}
		</div>
	);
};
